"use client";

import { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

/** 每屏并排展示的图片数量 */
const IMAGES_PER_SLIDE = 4;

export interface SheetMetalFabricationDisplayProps {
	/** next-intl 命名空间，需含 title、imageAltPrefix、carouselAriaLabel、goToSlide */
	translationNamespace?: string;
	/** 图片列表 */
	images: readonly string[];
	/** 自动轮播间隔（毫秒），0 表示不自动播放 */
	autoplayMs?: number;
}

/**
 * Sheet Metal Fabrication 产品展示轮播：每屏 4 张图并排，底部圆点切换（滑动窗口）
 */
export default function SheetMetalFabricationDisplay({
	translationNamespace = "sheetMetalFabricationDisplay",
	images,
	autoplayMs = 5000,
}: SheetMetalFabricationDisplayProps) {
	const t = useTranslations(translationNamespace);
	const [activeIndex, setActiveIndex] = useState(0);

	const { slideCount, slides } = useMemo(() => {
		const n = images.length;
		if (n === 0) return { slideCount: 0, slides: [] as string[][] };

		if (n <= IMAGES_PER_SLIDE) {
			return {
				slideCount: 1,
				slides: [images.slice()],
			};
		}

		const count = n - IMAGES_PER_SLIDE + 1;
		const slideList: string[][] = [];
		for (let start = 0; start < count; start++) {
			slideList.push(images.slice(start, start + IMAGES_PER_SLIDE) as string[]);
		}
		return { slideCount: count, slides: slideList };
	}, [images]);

	useEffect(() => {
		if (slideCount <= 1 || autoplayMs <= 0) return;
		const id = setInterval(() => {
			setActiveIndex((i) => (i + 1) % slideCount);
		}, autoplayMs);
		return () => clearInterval(id);
	}, [slideCount, autoplayMs]);

	if (images.length === 0 || slideCount === 0) return null;

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-6 text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
					{t("title")}
				</h2>

				{/* 每屏 4 图并排，横向滑动 */}
				<div className="overflow-hidden rounded-lg">
					<div
						className="flex transition-transform duration-500 ease-out"
						style={{ transform: `translateX(-${activeIndex * 100}%)` }}
					>
						{slides.map((slideImages, slideIdx) => (
							<div
								key={slideIdx}
								className="min-w-full shrink-0 px-1 py-3 sm:px-2 sm:py-4"
								aria-hidden={activeIndex !== slideIdx}
							>
								<div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4 md:gap-4">
									{slideImages.map((src, i) => {
										/* 原图序号：第 slideIdx 屏从第 slideIdx 张起，格内第 i 张 */
										const globalIndex = slideIdx + i;
										return (
											<div
												key={`${slideIdx}-${i}`}
												className="relative aspect-[4/3] w-full overflow-hidden rounded-md bg-gray-200"
											>
												<Image
													src={src}
													alt={`${t("imageAltPrefix")} ${globalIndex + 1}`}
													fill
													className="object-cover"
													sizes="(max-width: 768px) 50vw, 25vw"
													priority={slideIdx === 0 && i < 2}
												/>
											</div>
										);
									})}
								</div>
							</div>
						))}
					</div>
				</div>

				{/* 底部圆点：数量 = 可滑动屏数 */}
				{slideCount > 1 && (
					<div
						className="mt-6 flex justify-center gap-2.5"
						role="tablist"
						aria-label={t("carouselAriaLabel")}
					>
						{Array.from({ length: slideCount }).map((_, i) => (
							<button
								key={i}
								type="button"
								role="tab"
								aria-selected={activeIndex === i}
								aria-label={t("goToSlide", { slide: i + 1 })}
								onClick={() => setActiveIndex(i)}
								className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full p-2"
							>
								<span
									className={`block h-2.5 w-2.5 rounded-full transition-colors ${
										activeIndex === i ? "bg-themePrimary400" : "bg-[#e8f5e0] hover:bg-[#c8e6c9]"
									}`}
								/>
							</button>
						))}
					</div>
				)}
			</div>
		</section>
	);
}
